<script setup lang="ts">
import { computed } from 'vue';

import { EntityPresenterV2Mode } from '@satellite/composables/presenterV2EntityMode';
import { useCarrierFetcherV2 } from '@satellite/composables/useCarrierFetcherV2';
import { useCarrierDisplayV2 } from '@satellite/composables/useCarrierDisplayV2';
import CarrierDisplayV2 from '@satellite/components/elements/presenters/CarrierDisplayV2.vue';

import type { Maybe } from '@nova/core';
import type { PresenterPlaceholderData } from '@satellite/composables/presenterV2Placeholder';
import {
  mapCompanyDisplayFieldsToCarrier,
  type CompanyDisplayField
} from '@satellite/composables/useCompanyDisplayV2';
import type {
  CarrierCompanyDetails,
  CarrierUserDetails
} from '@satellite/composables/carrierPresenterV2Shared';

const {
  size = 'regular',
  initialCompanyData = null,
  placeholderCompanyData = null,
  initialCarrierData = null,
  placeholderCarrierData = null,
  staticCarrierData = null,
  staticCompanyData = null,
  companyId = null,
  carrierId = null,
  mode = EntityPresenterV2Mode.Live,
  displayFields = ['name'],
  nameProps = {},
  scacProps = {}
} = defineProps<{
  size?: 'regular' | 'small' | 'large';
  initialCompanyData?: Maybe<CarrierCompanyDetails>;
  placeholderCompanyData?: Maybe<PresenterPlaceholderData<CarrierCompanyDetails>>;
  initialCarrierData?: Maybe<CarrierUserDetails>;
  placeholderCarrierData?: Maybe<PresenterPlaceholderData<CarrierUserDetails>>;
  staticCarrierData?: Maybe<CarrierUserDetails>;
  staticCompanyData?: Maybe<CarrierCompanyDetails>;
  companyId?: Maybe<string>;
  carrierId?: Maybe<string>;
  mode?: EntityPresenterV2Mode;
  displayFields?: CompanyDisplayField[];
  nameProps?: Record<string, unknown>;
  scacProps?: Record<string, unknown>;
}>();

const resolvedStaticCarrierData = computed((): CarrierUserDetails | null => {
  if (staticCarrierData != null) {
    return staticCarrierData;
  }

  if (staticCompanyData != null) {
    return {
      id: staticCompanyData.id,
      company: staticCompanyData
    };
  }

  return null;
});

const {
  data: carrier,
  isLoading,
  isFetching,
  onIntersect
} = useCarrierFetcherV2({
  companyId: computed(() => companyId),
  carrierId: computed(() => carrierId),
  initialCarrierData: computed(() => initialCarrierData),
  placeholderCarrierData: computed(() => placeholderCarrierData),
  initialCompanyData: computed(() => initialCompanyData),
  placeholderCompanyData: computed(() => placeholderCompanyData),
  staticCarrierData: resolvedStaticCarrierData,
  mode
});

const data = useCarrierDisplayV2(carrier, {
  size: computed(() => size),
  mode: computed(() => mode),
  isFetching,
  companyNameProps: computed(() => nameProps),
  companyScacProps: computed(() => scacProps)
});

const resolvedDisplayFields = computed(() => mapCompanyDisplayFieldsToCarrier(displayFields));
</script>

<template>
  <v-skeleton-loader
    :loading="isLoading"
    type="text"
    v-intersect="{
      handler: onIntersect,
      options: {
        threshold: [0.25]
      }
    }"
    class="company-presenter-v2 bg-transparent">
    <carrier-display-v2
      :data="data"
      :display-fields="resolvedDisplayFields"
      column-gap-class="gap-1"
      :style="isLoading ? 'min-width: 50px' : ''">
      <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
        <slot :name="slotName" v-bind="slotProps ?? {}" />
      </template>
    </carrier-display-v2>
  </v-skeleton-loader>
</template>

<style scoped>
:deep(.v-skeleton-loader__text) {
  margin: 0 auto !important;
  height: 1em !important;
}
</style>
